import { useState, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Box,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  LinearProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SearchIcon from '@mui/icons-material/Search';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from '../settings/components/SettingsMenu';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import TemperatureChart from './TemperatureChart';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    maxWidth: '100%',
    width: '100%',
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  statsChips: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  deviceCard: {
    height: '100%',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  cardOk: {
    borderLeft: `4px solid ${theme.palette.success.main}`,
  },
  cardAlert: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: theme.palette.warning.light + '10',
  },
  cardCritical: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    backgroundColor: theme.palette.error.light + '10',
  },
  temperatureValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  deviceName: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  deviceInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  tabPanel: {
    paddingTop: theme.spacing(3),
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const TemperaturePage = () => {
  const { classes } = useStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const TEMP_MIN = 2;
  const TEMP_MAX = 8;
  const TEMP_CRITICAL_MIN = -5;
  const TEMP_CRITICAL_MAX = 15;

  useEffectAsync(async () => {
    try {
      const devicesResponse = await fetch('/api/devices');
      if (devicesResponse.ok) {
        const allDevices = await devicesResponse.json();
        setDevices(allDevices);

        const positionsResponse = await fetch('/api/positions');
        if (positionsResponse.ok) {
          const allPositions = await positionsResponse.json();
          const posMap = {};
          allPositions.forEach((pos) => {
            posMap[pos.deviceId] = pos;
          });
          setPositions(posMap);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getTemperature = (deviceId) => {
    const pos = positions[deviceId];
    if (!pos || !pos.attributes) return null;
    return pos.attributes.temp1 ?? pos.attributes.temp ?? pos.attributes.temperature ?? null;
  };

  const getStatus = (temp) => {
    if (temp === null) return 'unknown';
    if (temp >= TEMP_MIN && temp <= TEMP_MAX) return 'ok';
    if (temp < TEMP_CRITICAL_MIN || temp > TEMP_CRITICAL_MAX) return 'critical';
    return 'alert';
  };

  const devicesWithTemp = useMemo(() => {
    return devices
      .map((device) => {
        const temp = getTemperature(device.id);
        const status = getStatus(temp);
        const position = positions[device.id];
        return {
          ...device,
          temperature: temp,
          status,
          lastUpdate: position?.fixTime || device.lastUpdate,
          address: position?.address,
        };
      })
      .filter((device) => device.temperature !== null);
  }, [devices, positions]);

  const filteredDevices = useMemo(() => {
    return devicesWithTemp
      .filter((device) => {
        if (filter === 'ok') return device.status === 'ok';
        if (filter === 'alert') return device.status === 'alert' || device.status === 'critical';
        return true;
      })
      .filter((device) => {
        if (!search) return true;
        return device.name.toLowerCase().includes(search.toLowerCase());
      });
  }, [devicesWithTemp, filter, search]);

  const stats = useMemo(() => {
    const ok = devicesWithTemp.filter((d) => d.status === 'ok').length;
    const alert = devicesWithTemp.filter((d) => d.status === 'alert').length;
    const critical = devicesWithTemp.filter((d) => d.status === 'critical').length;
    return { total: devicesWithTemp.length, ok, alert, critical };
  }, [devicesWithTemp]);

  const getCardClass = (status) => {
    if (status === 'ok') return classes.cardOk;
    if (status === 'critical') return classes.cardCritical;
    if (status === 'alert') return classes.cardAlert;
    return '';
  };

  const getStatusColor = (status) => {
    if (status === 'ok') return 'success';
    if (status === 'critical') return 'error';
    if (status === 'alert') return 'warning';
    return 'default';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'temperatureMonitor']}>
      <Container maxWidth={false} className={classes.container}>
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label={t('sharedOverview')} />
              <Tab label={t('sharedDetails')} disabled={!selectedDevice} />
            </Tabs>

            {tab === 0 && (
              <>
                {/* Filters */}
                <Box className={classes.filterContainer}>
                  <TextField
                    size="small"
                    placeholder={t('sharedSearch')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <ToggleButtonGroup
                    size="small"
                    value={filter}
                    exclusive
                    onChange={(e, v) => v && setFilter(v)}
                  >
                    <ToggleButton value="all">{t('sharedAll')}</ToggleButton>
                    <ToggleButton value="ok">{t('statusOk')}</ToggleButton>
                    <ToggleButton value="alert">{t('statusAlert')}</ToggleButton>
                  </ToggleButtonGroup>

                  <Box className={classes.statsChips}>
                    <Chip
                      icon={<DeviceThermostatIcon />}
                      label={`${stats.total} ${t('deviceTitle')}`}
                      variant="outlined"
                    />
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`${stats.ok} ${t('statusOk')}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      icon={<WarningIcon />}
                      label={`${stats.alert + stats.critical} ${t('statusAlert')}`}
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                </Box>

                {/* Device Grid */}
                {filteredDevices.length > 0 ? (
                  <Grid container spacing={2}>
                    {filteredDevices.map((device) => (
                      <Grid key={device.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <Card
                          className={`${classes.deviceCard} ${getCardClass(device.status)}`}
                          onClick={() => {
                            setSelectedDevice(device);
                            setTab(1);
                          }}
                        >
                          <CardContent>
                            <Typography className={classes.deviceName} noWrap>
                              {device.name}
                            </Typography>
                            <Box className={classes.temperatureValue}>
                              <ThermostatIcon color={getStatusColor(device.status)} />
                              <span style={{ color: device.status === 'ok' ? 'inherit' : device.status === 'critical' ? tokens.semantic.error : tokens.semantic.warning }}>
                                {device.temperature?.toFixed(1)}°C
                              </span>
                            </Box>
                            <Typography className={classes.deviceInfo}>
                              {formatDate(device.lastUpdate)}
                            </Typography>
                            <Typography className={classes.deviceInfo} noWrap>
                              {device.address || t('sharedNoAddress')}
                            </Typography>
                            <Box mt={1}>
                              <Chip
                                size="small"
                                label={device.status === 'ok' ? t('statusOk') : t('statusAlert')}
                                color={getStatusColor(device.status)}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Paper className={classes.noData}>
                    <ThermostatIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6">{t('sharedNoData')}</Typography>
                    <Typography color="textSecondary">
                      {devicesWithTemp.length === 0
                        ? 'Nenhum dispositivo com sensor de temperatura encontrado'
                        : 'Nenhum dispositivo corresponde aos filtros'}
                    </Typography>
                  </Paper>
                )}
              </>
            )}

            {tab === 1 && selectedDevice && (
              <Box className={classes.tabPanel}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Box>
                    <Typography variant="h5">{selectedDevice.name}</Typography>
                    <Typography color="textSecondary">{selectedDevice.address || t('sharedNoAddress')}</Typography>
                  </Box>
                  <Box className={classes.temperatureValue}>
                    <ThermostatIcon color={getStatusColor(selectedDevice.status)} fontSize="large" />
                    <span>{selectedDevice.temperature?.toFixed(1)}°C</span>
                  </Box>
                </Box>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('temperatureHistory')}
                  </Typography>
                  <TemperatureChart deviceId={selectedDevice.id} />
                </Paper>
              </Box>
            )}
          </>
        )}
      </Container>
    </PageLayout>
  );
};

export default TemperaturePage;
